/**
 * Products Table Component for Admin Dashboard
 * Displays and manages products with filtering, sorting, and pagination
 */

import { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import type { Product, ProductListItem, ProductCategory } from '../../../types';
import ProductStatusBadge from './ProductStatusBadge';
import ProductActionsMenu from './ProductActionsMenu';
import ProductQuickViewModal from '../../products/ProductQuickViewModal';
import { getProductCategories } from '../../../services/productsApi';
import './ProductsTable.css';

const API_BASE = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || 'http://localhost:8787';

interface ProductsTableProps {
  onEdit?: (product: ProductListItem) => void;
  onBatchAction?: (action: 'publish' | 'unpublish' | 'delete', productIds: string[]) => Promise<void> | void;
}

const ProductsTable = ({ onEdit, onBatchAction }: ProductsTableProps) => {
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [operatingIds, setOperatingIds] = useState<Set<string>>(new Set());
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());

  // Quick view state
  const [selectedProduct, setSelectedProduct] = useState<ProductListItem | Product | null>(null);
  const [showQuickView, setShowQuickView] = useState(false);

  // Batch selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  const limit = 20;

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [page, categoryFilter, statusFilter, searchQuery]);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const loadCategories = async () => {
    try {
      const response = await getProductCategories();
      if (response.success) {
        setCategories(response.data);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);

      // Add timestamp to avoid browser cache
      params.append('_t', Date.now().toString());

      const response = await fetch(`${API_BASE}/api/admin/products?${params}`, {
        headers: getAuthHeaders(),
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setProducts(data.data);
        setTotal(data.pagination?.total || 0);
      } else {
        throw new Error(data.error?.message || '載入商品資料失敗');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '載入商品時發生錯誤';
      console.error('Error loading products:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const addToOperating = (id: string) => {
    setOperatingIds(prev => new Set([...prev, id]));
  };

  const removeFromOperating = (id: string) => {
    setOperatingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const addToPendingDeletes = (...ids: string[]) => {
    setPendingDeletes(prev => new Set([...prev, ...ids]));
  };

  const isOperating = (id: string) => operatingIds.has(id);
  const isPendingDelete = (id: string) => pendingDeletes.has(id);

  const handleEdit = (product: ProductListItem) => {
    if (onEdit) {
      onEdit(product);
    }
  };

  // Quick view handlers
  const handleQuickView = (product: ProductListItem | Product) => {
    setSelectedProduct(product);
    setShowQuickView(true);
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
    setSelectedProduct(null);
  };

  const handleEditProduct = (product: ProductListItem | Product) => {
    handleCloseQuickView();
    if (onEdit) {
      // ProductListItem has a subset of Product properties, so we can safely pass it
      onEdit(product as ProductListItem);
    }
  };

  // Reserved for future status change functionality
  // const _handleStatusChange = async (id: string, newStatus: string) => {
  //   const product = products.find(p => p.id === id);
  //   if (!product) return;
  //
  //   const oldStatus = product.status;
  //
  //   // Optimistic update
  //   setProducts(prev =>
  //     prev.map(p => (p.id === id ? { ...p, status: newStatus as 'publish' | 'draft' } : p))
  //   );
  //   addToOperating(id);
  //
  //   try {
  //     const response = await fetch(`${API_BASE}/api/admin/products/${id}`, {
  //       method: 'PATCH',
  //       headers: getAuthHeaders(),
  //       body: JSON.stringify({ status: newStatus }),
  //     });
  //
  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({}));
  //       throw new Error(errorData.error?.message || `HTTP ${response.status}: 更新狀態失敗`);
  //     }
  //   } catch (err) {
  //     // Revert on error
  //     setProducts(prev =>
  //       prev.map(p => (p.id === id ? { ...p, status: oldStatus as 'publish' | 'draft' } : p))
  //     );
  //     const message = err instanceof Error ? err.message : '更新狀態時發生錯誤';
  //     setError(message);
  //     alert(`更新失敗: ${message}`);
  //   } finally {
  //     removeFromOperating(id);
  //   }
  // };

  const handleDelete = async (id: string) => {
    addToOperating(id);
    addToPendingDeletes(id);

    // Wait for fade-out animation
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const response = await fetch(`${API_BASE}/api/products/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok && response.status !== 204) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 刪除失敗`);
      }

      // Remove from local list
      setProducts(prev => prev.filter(p => p.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Cancel fade-out on error
      setPendingDeletes(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const message = err instanceof Error ? err.message : '刪除時發生錯誤';
      setError(message);
      alert(`刪除失敗: ${message}`);
    } finally {
      removeFromOperating(id);
    }
  };

  const getDisplayPrice = (product: ProductListItem) => {
    if (product.salePrice !== null) {
      return (
        <>
          <span className="products-table__price-original">
            NT${product.regularPrice?.toLocaleString()}
          </span>
          <span className="products-table__price-sale">NT${product.salePrice.toLocaleString()}</span>
        </>
      );
    }
    return <span>NT${product.regularPrice?.toLocaleString() || '-'}</span>;
  };

  // Selection handlers
  const handleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setIsAllSelected(false);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected || selectedIds.size === products.length) {
      // Deselect all
      setSelectedIds(new Set());
      setIsAllSelected(false);
    } else {
      // Select all visible
      setSelectedIds(new Set(products.map(p => p.id)));
      setIsAllSelected(true);
    }
  };

  const handleBatchPublish = () => {
    if (onBatchAction && selectedIds.size > 0) {
      onBatchAction('publish', Array.from(selectedIds));
    }
  };

  const handleBatchUnpublish = () => {
    if (onBatchAction && selectedIds.size > 0) {
      onBatchAction('unpublish', Array.from(selectedIds));
    }
  };

  const handleBatchDelete = () => {
    if (onBatchAction && selectedIds.size > 0) {
      onBatchAction('delete', Array.from(selectedIds));
    }
  };

  if (loading) {
    return (
      <>
        {selectedProduct && (
          <ProductQuickViewModal
            product={selectedProduct}
            mode="admin"
            isOpen={showQuickView}
            onClose={handleCloseQuickView}
            onEdit={handleEditProduct}
          />
        )}
        <div className="products-table__loading">載入中...</div>
      </>
    );
  }

  if (error && products.length === 0) {
    return (
      <>
        {selectedProduct && (
          <ProductQuickViewModal
            product={selectedProduct}
            mode="admin"
            isOpen={showQuickView}
            onClose={handleCloseQuickView}
            onEdit={handleEditProduct}
          />
        )}
        <div className="products-table">
          <div className="products-table__error">
            <p>載入失敗: {error}</p>
            <button onClick={loadProducts} className="btn btn--sm">
              重新載入
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* ProductQuickViewModal */}
      {selectedProduct && (
        <ProductQuickViewModal
          product={selectedProduct}
          mode="admin"
          isOpen={showQuickView}
          onClose={handleCloseQuickView}
          onEdit={handleEditProduct}
        />
      )}

      {/* Products Table */}
      <div className="products-table">
      {/* Filters */}
      <div className="products-table__filters">
        <input
          type="text"
          placeholder="搜尋商品名稱或 SKU..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="products-table__search"
        />
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="products-table__filter-select"
        >
          <option value="">所有分類</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="products-table__filter-select"
        >
          <option value="">所有狀態</option>
          <option value="publish">已發布</option>
          <option value="draft">草稿</option>
        </select>
      </div>

      {/* Table */}
      <div className="products-table__wrapper">
        <table className="products-table__table">
          <thead className="products-table__thead">
            <tr>
              {onBatchAction && (
                <th className="products-table__th products-table__th--checkbox">
                  <input
                    type="checkbox"
                    checked={isAllSelected || selectedIds.size === products.length}
                    onChange={handleSelectAll}
                    className="products-table__checkbox"
                  />
                </th>
              )}
              <th className="products-table__th">圖片</th>
              <th className="products-table__th">商品名稱</th>
              <th className="products-table__th">SKU</th>
              <th className="products-table__th">分類</th>
              <th className="products-table__th">價格</th>
              <th className="products-table__th">庫存</th>
              <th className="products-table__th">狀態</th>
              <th className="products-table__th">瀏覽</th>
              <th className="products-table__th">快速查看</th>
              <th className="products-table__th">建立時間</th>
              <th className="products-table__th products-table__th--actions">操作</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan={onBatchAction ? 12 : 11} className="products-table__empty">
                  沒有商品資料
                </td>
              </tr>
            ) : (
              products.map(product => (
                <tr
                  key={product.id}
                  className={`products-table__row ${
                    isPendingDelete(product.id) ? 'products-table__row--deleting' : ''
                  } ${selectedIds.has(product.id) ? 'products-table__row--selected' : ''}`}
                >
                  {onBatchAction && (
                    <td className="products-table__td">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(product.id)}
                        onChange={() => handleSelectOne(product.id)}
                        className="products-table__checkbox"
                      />
                    </td>
                  )}
                  <td className="products-table__td">
                    {product.featuredImageUrl || product.imageUrls?.[0] ? (
                      <img
                        src={product.featuredImageUrl || product.imageUrls[0]}
                        alt={product.title}
                        className="products-table__thumbnail"
                        onClick={() => handleQuickView(product)}
                        style={{ cursor: 'pointer' }}
                        title="點擊查看詳情"
                      />
                    ) : (
                      <div className="products-table__thumbnail products-table__thumbnail--empty">-</div>
                    )}
                  </td>
                  <td className="products-table__td">
                    <div className="products-table__title">{product.title}</div>
                    {product.shortDescription && (
                      <div className="products-table__description">
                        {product.shortDescription}
                      </div>
                    )}
                  </td>
                  <td className="products-table__td">{product.sku || '-'}</td>
                  <td className="products-table__td">{product.productCategory.split(',').map(c => c.trim()).filter(c => c)[0] || product.productCategory}</td>
                  <td className="products-table__td">{getDisplayPrice(product)}</td>
                  <td className="products-table__td">
                    <span
                      className={`products-table__stock ${
                        product.stockStatus === 'outofstock'
                          ? 'products-table__stock--out'
                          : product.stockStatus === 'onbackorder'
                            ? 'products-table__stock--backorder'
                            : ''
                      }`}
                    >
                      {product.stockStatus === 'outofstock'
                        ? '缺貨'
                        : product.stockStatus === 'onbackorder'
                          ? '預訂'
                          : '有庫存'}
                    </span>
                  </td>
                  <td className="products-table__td">
                    <ProductStatusBadge status={product.status} />
                  </td>
                  <td className="products-table__td">{product.viewCount}</td>
                  <td className="products-table__td" style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="action-btn action-btn--quick-view"
                      onClick={() => handleQuickView(product)}
                      title="快速查看"
                      aria-label="快速查看"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                  <td className="products-table__td">
                    {new Date(product.createdAt).toLocaleDateString('zh-TW')}
                  </td>
                  <td className="products-table__td">
                    <ProductActionsMenu
                      productId={product.id}
                      productTitle={product.title}
                      onEdit={() => handleEdit(product)}
                      onDelete={handleDelete}
                      isOperating={isOperating(product.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Error bar */}
      {error && products.length > 0 && (
        <div className="products-table__error-bar">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="btn btn--sm">
            關閉
          </button>
        </div>
      )}

      {/* Batch Actions Bar */}
      {onBatchAction && selectedIds.size > 0 && (
        <div className="products-table__batch-actions">
          <span className="products-table__batch-count">
            已選擇 {selectedIds.size} 個商品
          </span>
          <div className="products-table__batch-buttons">
            <button
              onClick={handleBatchPublish}
              className="btn btn--sm products-table__batch-btn products-table__batch-btn--publish"
            >
              發布
            </button>
            <button
              onClick={handleBatchUnpublish}
              className="btn btn--sm products-table__batch-btn products-table__batch-btn--unpublish"
            >
              取消發布
            </button>
            <button
              onClick={handleBatchDelete}
              className="btn btn--sm products-table__batch-btn products-table__batch-btn--delete"
            >
              刪除
            </button>
          </div>
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="products-table__pagination">
          <div className="products-table__page-info">
            顯示 {page * limit + 1} - {Math.min((page + 1) * limit, total)} / 共 {total} 項
          </div>
          <div className="products-table__page-controls">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="btn btn--sm"
            >
              上一頁
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="btn btn--sm"
            >
              下一頁
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ProductsTable;
